<script setup lang="ts">
	import { useQueryClient } from '@tanstack/vue-query'
	import { log } from 'console'
	import L from 'leaflet'
	import { computed, nextTick, onUnmounted, reactive, ref } from 'vue'
	import { onMounted } from 'vue'
	import { watch } from 'vue'
	import { useRoute } from 'vue-router'
	import { useToast } from 'vue-toastification'

	import ImageCropper from '@/components/ImageCropper.vue'
	import RoundedBox from '@/components/RoundedBox.vue'
	import StyledButton from '@/components/StyledButton.vue'
	import StyledCombobox from '@/components/StyledCombobox.vue'
	import StyledDropZone from '@/components/StyledDropZone.vue'

	import { useSearchPlaces } from '@/composables/useSearchPlaces'

	import useHotels from '@/internal/modules/providers/composables/useHotels'
	import { Hotel } from '@/internal/modules/providers/interfaces/Hotel'

	import { usePlaceDetails } from '@/modules/hotels/composables/usePlaceDetails'

	import useHotelMedia from '../composables/useHotelMedia'

	interface Props {
		hotel: Hotel
	}

	const props = defineProps<Props>()

	const route = useRoute()
	const queryClient = useQueryClient()

	const toast = useToast()

	const media = reactive<{
		file: File | null
		name: string
		collectionName: string
		hotelId: number
	}>({
		file: null,
		name: '',
		collectionName: 'facade',
		hotelId: Number(route.params.id),
	})

	const { updateHotelAsyncMutation } = useHotels({
		immediate: false,
		params: {
			id: Number(route.params.id),
		},
	})

	const {
		data: medias,
		createMediaHotelAsyncMutation,
		createMediaHotelLoading,
		isLoading,
		refetch,
	} = useHotelMedia({
		immediate: true,
		params: {
			id: Number(route.params.id),
		},
	})

	const map = ref<L.Map | null>(null)
	let marker: L.Marker | null = null

	const initializeMap = (latitude: number, longitude: number) => {
		const mapContainer = document.getElementById('map')

		if (!map.value && mapContainer) {
			map.value = L.map(mapContainer).setView([latitude, longitude], 20)

			map.value.invalidateSize()

			L.tileLayer(
				'https://api.mapbox.com/styles/v1/desarrolloef/clprhwrxy00d001r726l45div/tiles/{z}/{x}/{y}?access_token=pk.eyJ1IjoiZGVzYXJyb2xsb2VmIiwiYSI6ImNsbmRxbjNoczA2YXIya2xmZ2tvc2s0NDMifQ.A5_U8g192aPpNMa0h8WWxw',
				{ attribution: '' }
			).addTo(map.value as L.Map)

			document.querySelector('.leaflet-control-attribution')?.remove()

			marker = L.marker([latitude, longitude])
				.addTo(map.value as L.Map)
				.bindPopup(props.hotel.name)
				.openPopup()
		}
	}

	const addMap = (latitude: number, longitude: number) => {
		if (!props.hotel.latitude || !props.hotel.longitude) return
		initializeMap(latitude, longitude)
	}

	const addNewMarker = (latitude: number, longitude: number) => {
		if (!map.value) {
			initializeMap(latitude, longitude)
			return
		}

		if (marker) {
			marker
				.setLatLng([latitude, longitude])
				.bindPopup(props.hotel.name)
				.openPopup()
			map.value.setView([latitude, longitude], 20)
		} else {
			marker = L.marker([latitude, longitude])
				.addTo(map.value as L.Map)
				.bindPopup(props.hotel.name)
				.openPopup()
			map.value.setView([latitude, longitude], 20)
		}
	}

	const facadeMedia = computed(() => {
		return medias.value?.find((media) => media.collectionName === 'facade')
	})

	// Image Cropper state
	const showCropper = ref(false)
	const imageToCrop = ref<string>('')
	const tempImageFile = ref<File | null>(null)

	// Watch para detectar cambios en media.file
	watch(
		() => media.file,
		(newFile) => {
			if (
				newFile &&
				newFile instanceof File &&
				newFile !== tempImageFile.value
			) {
				tempImageFile.value = newFile
				const reader = new FileReader()
				reader.onload = (e) => {
					imageToCrop.value = e.target?.result as string
					showCropper.value = true
				}
				reader.readAsDataURL(newFile)
			}
		}
	)

	// Manejar cuando se aplica el recorte
	const handleCropApplied = async (croppedFile: File) => {
		tempImageFile.value = croppedFile
		media.file = croppedFile
		showCropper.value = false

		// Hacer la petición automáticamente después de aplicar el recorte
		await handleUpload()
	}

	// Manejar cuando se cancela el recorte
	const handleCropCancelled = () => {
		showCropper.value = false
		media.file = null
		tempImageFile.value = null
	}

	const editing = ref(false)
	const handleUpload = async () => {
		if (!media.file) {
			toast.error('Por favor selecciona una imagen')
			return
		}

		const data = await createMediaHotelAsyncMutation({
			id: media.hotelId,
			media: media.file,
			collection: media.collectionName,
			expirationDate: '',
		})

		if (data) {
			editing.value = false
			media.file = null
			media.name = ''

			await refetch()
		} else {
			toast.error('Error al subir la imagen')
		}
	}

	const handleCancel = () => {
		editing.value = false
	}

	const editAddres = ref(false)

	// Google Places Search
	const {
		setSearchPlaceQuery,
		isFetching: isFetchingPlaces,
		suggestionPlaces,
		setSelectedPlace: setSelectedPlaceById,
	} = useSearchPlaces({
		params: {
			includedPrimaryTypes: [
				'street_address',
				'route',
				'establishment',
				'geocode',
				'locality',
			],
		},
	})

	const { retryDetails } = usePlaceDetails()

	const selectedAddress = ref<{ id: string; name: string } | null>(null)

	const transformedPlacesResults = computed(() => {
		return (
			suggestionPlaces?.value?.map((place) => ({
				name: place.placePrediction?.text?.text,
				id: place?.placePrediction.placeId,
			})) ?? []
		)
	})

	const onRemoveAddress = () => {
		selectedAddress.value = null
		setSearchPlaceQuery('')
	}

	const handleSelectAddress = (
		place: { id: string; name: string | undefined } | undefined
	) => {
		if (!place || !place.name) return
		selectedAddress.value = { id: place.id, name: place.name }
	}

	const savingAddress = ref(false)

	const handleSaveAddress = async () => {
		if (!selectedAddress.value) return

		setSelectedPlaceById({ placeId: selectedAddress.value.id })
		savingAddress.value = true

		const details = await retryDetails()

		if (details?.data) {
			try {
				await updateHotelAsyncMutation({
					id: Number(route.params.id),
					latitude: details.data.location.latitude,
					longitude: details.data.location.longitude,
					address: selectedAddress.value.name,
				})

				queryClient.invalidateQueries(['hotel', props.hotel.id])

				editAddres.value = false
				selectedAddress.value = null

				showMap.value = true
				await nextTick()

				addNewMarker(
					details.data.location.latitude,
					details.data.location.longitude
				)
			} catch (err: any) {
				toast.error(
					err.response?.data?.message ?? 'Error al actualizar la dirección'
				)

				console.log(err)
			} finally {
				savingAddress.value = false
				await refetch()
			}
		} else {
			savingAddress.value = false
		}
	}

	onMounted(() => {
		if (props.hotel.latitude && props.hotel.longitude) {
			addMap(Number(props.hotel.latitude), Number(props.hotel.longitude))
		}
	})

	onUnmounted(() => {
		if (map.value) {
			map.value.remove()
			map.value = null
		}
	})

	const fullAddress = computed(() => {
		return props.hotel.address
	})

	const showMap = ref(false)

	// Inicializar showMap basado en si el hotel tiene coordenadas
	watch(
		() => props.hotel.address,
		(newAddress) => {
			if (newAddress) {
				showMap.value = true
			}
		},
		{ immediate: true }
	)
</script>

<template>
	<RoundedBox class="bg-white p-6">
		<!-- Facade Section -->
		<div class="flex flex-col gap-6">
			<!-- Header Section -->
			<div class="flex items-start justify-between">
				<div class="flex flex-col gap-2">
					<div class="flex items-center gap-3">
						<div class="p-2 bg-blue-100 rounded-lg">
							<svg
								class="w-6 h-6 text-blue-600"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
								/>
							</svg>
						</div>
						<h1 class="text-2xl font-bold text-gray-800">Fachada del Hotel</h1>
					</div>

					<p class="text-sm text-gray-600 ml-14">
						{{
							facadeMedia?.originalUrl ?
								'Cambia la imagen de la fachada del hotel si no es la correcta'
							:	`Sube una imagen de la fachada del hotel para que los clientes puedan reconocerlo fácilmente`
						}}
					</p>
				</div>

				<!-- Status Badge -->
				<span
					v-if="facadeMedia"
					class="px-2 py-1 text-xs font-semibold text-green-700 bg-green-100 rounded-full text-center"
				>
					✓ Imagen cargada
				</span>
				<span
					v-else
					class="px-2 py-1 text-xs font-semibold text-amber-700 bg-amber-100 rounded-full text-center"
				>
					⚠ Sin imagen
				</span>
			</div>

			<!-- Image Display Area -->
			<div class="relative">
				<template v-if="facadeMedia">
					<div
						v-if="!editing && !createMediaHotelLoading"
						class="relative group"
					>
						<img
							:src="facadeMedia?.originalUrl"
							alt="Fachada del hotel"
							class="w-full h-64 object-cover rounded-xl shadow-lg transition-transform duration-300 group-hover:scale-[1.02]"
						/>
						<div
							class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-xl transition-all duration-300"
						></div>
					</div>

					<template v-else>
						<!-- Input for the file  -->
						<StyledDropZone
							v-if="!createMediaHotelLoading"
							v-model="media.file"
							:acceptTypes="[
								'image/png',
								'image/jpg',
								'image/jpeg',
								'image/gif',
							]"
							label="Arrastra la imagen aquí o haz clic para seleccionar"
							labelClass="text-sm font-semibold text-gray-700"
							dropZoneClass="h-64"
							class="rounded-xl border-2 border-dashed border-gray-300 hover:border-blue-500 transition-colors"
							:maxKb="20480"
						/>

						<!-- Loading for the change the image -->
						<div
							v-else
							class="w-full h-64 object-cover rounded-xl animate-pulse bg-gradient-to-r from-blue-100 to-blue-200 shadow-lg"
						>
							<div
								class="flex flex-col items-center justify-center h-full text-gray-700 gap-3"
							>
								<svg
									class="w-10 h-10 text-blue-600 animate-spin"
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
									stroke-width="2"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
									/>
								</svg>
								<p class="text-lg font-semibold text-blue-700">
									Subiendo imagen de la fachada...
								</p>
								<p class="text-sm text-gray-600">
									Por favor, espera un momento
								</p>
							</div>
						</div>
					</template>

					<p
						v-if="facadeMedia?.name"
						class="mt-3 text-sm text-gray-500 flex items-center gap-2"
					>
						<svg
							class="w-4 h-4"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
							/>
						</svg>
						{{ facadeMedia?.name }}
					</p>
				</template>

				<template v-else>
					<!-- When the hotel has no image -->
					<StyledDropZone
						v-if="!isLoading"
						v-model="media.file"
						:acceptTypes="['image/png', 'image/jpg', 'image/jpeg', 'image/gif']"
						label="Arrastra la imagen aquí o haz clic para seleccionar"
						labelClass="text-sm font-semibold text-gray-700"
						dropZoneClass="h-64"
						class="rounded-xl border-2 border-dashed border-gray-300 hover:border-blue-500 transition-colors"
						:maxKb="20480"
						:have-preview="true"
					/>

					<!-- Loading Imagen hotel -->
					<div
						class="flex flex-col items-center justify-center h-64 bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl gap-3"
						v-else
					>
						<svg
							class="w-10 h-10 text-gray-600 animate-spin"
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
							stroke-width="2"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
							/>
						</svg>
						<p class="text-lg font-semibold text-gray-700">
							Cargando imagen de la fachada...
						</p>
					</div>
				</template>
			</div>

			<!-- Image Cropper Modal -->
			<ImageCropper
				v-model:isVisible="showCropper"
				:imageSrc="imageToCrop"
				cropMode="horizontal"
				:aspectRatioLocked="false"
				:uploading="createMediaHotelLoading"
				@crop-applied="handleCropApplied"
				@crop-cancelled="handleCropCancelled"
			/>

			<!-- Action Buttons -->
			<div class="flex gap-3 justify-start">
				<StyledButton
					v-if="facadeMedia"
					@click="
						() => {
							editing = !editing

							if (editing) return
							handleUpload()
						}
					"
					class="px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
					:loading="createMediaHotelLoading"
				>
					<svg
						class="w-4 h-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
						/>
					</svg>
					{{ editing ? 'Guardar cambios' : 'Cambiar imagen' }}
				</StyledButton>

				<StyledButton
					v-else
					@click="handleUpload"
					class="px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
					:loading="createMediaHotelLoading"
				>
					<svg
						class="w-4 h-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
						/>
					</svg>
					Subir imagen
				</StyledButton>

				<StyledButton
					v-if="editing"
					@click="handleCancel"
					class="px-6 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 shadow-md hover:shadow-lg transition-all duration-300"
				>
					Cancelar
				</StyledButton>
			</div>
		</div>

		<!-- Divider -->
		<div class="my-8 border-t-2 border-gray-200"></div>

		<!-- Address Section -->
		<div class="flex flex-col gap-6">
			<!-- Header Section -->
			<div class="flex items-center justify-between">
				<div class="flex flex-col gap-2">
					<div class="flex items-center gap-3">
						<div class="p-2 bg-green-100 rounded-lg">
							<svg
								class="w-6 h-6 text-green-600"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
								/>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
								/>
							</svg>
						</div>
						<h1 class="text-2xl font-bold text-gray-800 text-center">
							Dirección del Hotel
						</h1>
					</div>

					<p class="text-sm text-gray-600 ml-14">
						{{
							!fullAddress ?
								'Por favor, agregue la dirección del hotel para facilitar su localización a los clientes.'
							:	'La dirección del hotel es fundamental para que los clientes puedan ubicarlo con precisión.'
						}}
					</p>
				</div>

				<!-- Status Badge -->
				<span
					v-if="fullAddress"
					class="px-2 py-1 text-xs font-semibold text-green-700 bg-green-100 rounded-full text-center"
				>
					✓ Dirección configurada
				</span>
				<span
					v-else
					class="px-2 py-1 text-xs font-semibold text-amber-700 bg-amber-100 rounded-full text-center"
				>
					⚠ Sin dirección
				</span>
			</div>

			<!-- Map Container - siempre existe pero se oculta cuando no hay dirección -->
			<div
				class="bg-gray-50 p-4 rounded-xl border border-gray-200 mb-4"
				:class="{ hidden: !showMap }"
			>
				<div
					id="map"
					class="w-full h-[450px] rounded-lg shadow-lg"
				></div>
			</div>

			<!-- Address Form -->
			<div class="bg-gray-50 p-6 rounded-xl border border-gray-200">
				<StyledCombobox
					v-model="selectedAddress"
					:label="hotel.address ? 'Cambiar dirección' : 'Buscar dirección'"
					:placeholder="
						hotel.address ?
							'Escribe una nueva dirección...'
						:	'Escribe una dirección...'
					"
					:options="transformedPlacesResults"
					:loading-options="isFetchingPlaces"
					:show-input="
						hotel.address ? !selectedAddress && editAddres : !selectedAddress
					"
					:debounce-delay="500"
					:on-input-change="(value: string) => setSearchPlaceQuery(value)"
					:after-model-change="handleSelectAddress"
				>
					<template #default="{ option }">
						<div class="flex items-center gap-2">
							<svg
								class="w-4 h-4 text-gray-500 flex-shrink-0"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
								/>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									sroke-width="2"
									d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
								/>
							</svg>
							<span class="text-sm">{{ option.name }}</span>
						</div>
					</template>

					<template #input-overlap>
						<template
							v-if="
								hotel.address ? selectedAddress || !editAddres : selectedAddress
							"
						>
							<p class="truncate text-sm normal-case">
								{{ selectedAddress?.name || props.hotel.address }}
							</p>
							<button
								v-if="selectedAddress"
								class="material-symbols-outlined text-[22px]"
								@click="onRemoveAddress"
							>
								close
							</button>
						</template>
					</template>
				</StyledCombobox>

				<div class="flex gap-3 mt-4">
					<!-- Botón para agregar dirección cuando no hay dirección -->
					<StyledButton
						v-if="!hotel.address && selectedAddress"
						class="px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-green-600 to-green-700 rounded-lg hover:from-green-700 hover:to-green-800 shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
						:loading="savingAddress"
						@click.prevent="handleSaveAddress"
					>
						<svg
							class="w-4 h-4"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M12 6v6m0 0v6m0-6h6m-6 0H6"
							/>
						</svg>
						Agregar dirección
					</StyledButton>

					<!-- Botón guardar cambios cuando ya tiene dirección y está editando -->
					<StyledButton
						v-if="hotel.address && editAddres"
						class="px-6 py-2.5 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-400 shadow-md hover:shadow-lg transition-all duration-300"
						:loading="savingAddress"
						@click.prevent="handleSaveAddress"
					>
						Guardar cambios
					</StyledButton>

					<!-- Botón editar/cancelar cuando ya tiene dirección -->
					<StyledButton
						v-if="hotel.address"
						class="px-6 py-2.5 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-400 shadow-md hover:shadow-lg transition-all duration-300"
						@click="() => (editAddres = !editAddres)"
					>
						{{ !editAddres ? 'Editar dirección' : 'Cancelar' }}
					</StyledButton>
				</div>
			</div>
		</div>
	</RoundedBox>
</template>
